// @flow strict

import React, { Suspense, useMemo, lazy } from "react";
import "./index.css";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { BrowserRouter, Redirect, Switch, Route, useLocation } from "react-router-dom";
import PrivateRoute from "~shared/components/privateRoute";
import Sidebar from "../shared/components/sidebar";
import { getCookie } from "~shared/utils/cookie";
import { ToastContainer } from "react-toastify";
import Header from "~shared/components/header";
import { Spinner } from "react-bootstrap";

const BookingConfiguration = lazy(() => import("../modules/bookingConfiguration/container"));
const ServiceTypes = lazy(() => import("../modules/serviceTypes/container"));
const SetGeneralPetSizes = lazy(() => import("../modules/setGeneralPetSizes/container"));
const PetType = lazy(() => import("../modules/petType/petType/container"));
const PetWeight = lazy(() => import("../modules/petType/petWeight/container"));
const PetHairLength = lazy(() => import("../modules/petType/petHairLength/container"));
const PetHairTexture = lazy(() => import("../modules/petType/petHairTexture/container"));
const PetDesiredHairLength = lazy(() => import("../modules/petType/petDesiredHairLength/container"));
const PetCombsBlades = lazy(() => import("../modules/petType/petCombsBlades/container"));
const PetName = lazy(() => import("../modules/petType/petName/container"));
const PetTemperament = lazy(() => import("../modules/petType/petTemperament/container"));
const PetVaccinationRecords = lazy(() => import("../modules/petType/petVaccinationRecords/container"));
const PetAllergies = lazy(() => import("../modules/petType/petAllergies/container"));
const PetVet = lazy(() => import("../modules/petType/petVet/container"));
const PetDocs = lazy(() => import("../modules/petType/petDocs/container"));
const PetEmergencyContact = lazy(() => import("../modules/petType/petEmergencyContact/container"));
const PetDeceaseDate = lazy(() => import("../modules/petType/petDeceaseDate/container"));
const FeedingInformation = lazy(() => import("../modules/feedingInformation/container"));
const ExtendedBehaviorInformation = lazy(() => import("../modules/extendedBehaviorInformation/container"));
const ShampooSelections = lazy(() => import("../modules/shampooSelections/container"));
const CologneSelections = lazy(() => import("../modules/cologneSelections/container"));
const AddNewVenue = lazy(() => import("../modules/venues/addNewVenue/container"));
const AddNewService = lazy(() => import("../modules/myServices/addNewService/container"));
const AddOnServices = lazy(() => import("../modules/addOnServices/container"));
const BookAppointment = lazy(() => import("../modules/appointment/bookAppointment/container"));
const AttendantList = lazy(() => import("../modules/attendant/attendantList/container"));
const AddNewAttendant = lazy(() => import("../modules/attendant/addNewAttendant/container"));
const EditAttendant = lazy(() => import("../modules/attendant/editAttendant/container"));
const PetTypeInformation = lazy(() => import("../modules/petType/petTypeInformation/container"));
const Success = lazy(() => import("../modules/success/container"));
const ErrorPage = lazy(() => import("../modules/ErrorPage/container"));
const CustomerPetDetails = lazy(() => import("../modules/customerPet/customerPetDetails/container"));
const CustomerPetList = lazy(() => import("../modules/customerPet/customerPetList/container"));
const CustomerAddNewPet = lazy(() => import("../modules/customerPet/addNewPet/container"));
const VenueList = lazy(() => import("../modules/venueList/container"));
const EditVenue = lazy(() => import("../modules/venues/editVenue/container"));
const ServicesList = lazy(() => import("../modules/myServices/serviceList/container"));
const EditService = lazy(() => import("../modules/myServices/editService/container"));
const CustomerServicesHistoryList = lazy(() => import("../modules/customerServiceHistory/customerServiceHistoryList/container"));
const CustomerServicesHistoryDetailsGeneral = lazy(() => import("../modules/customerServiceHistory/customerServiceHistoryDetailsGeneral/container"));
const CustomerServicesHistoryDetailsNotes = lazy(() => import("../modules/customerServiceHistory/customerServiceHistoryDetailsNotes/container"));
const Schedule = lazy(() => import("../modules/schedule/calendar/container"));
const Availability = lazy(() => import("../modules/schedule/newAVailabilityPage/container"));
const Appointments = lazy(() => import("../modules/Appointments/AppointmentsList"));
const TodaysAppointmentList = lazy(() => import("~/app/modules/schedule/todaysAppointmentList/container"));
const TodaysAppointmentDetails = lazy(() => import("~/app/modules/schedule/todaysAppointmentDetails/container"));
const BatchActions = lazy(() => import("~/app/modules/schedule/batchActions/container"));
const GoogleCalendarSyncCollab = lazy(() => import("~/app/modules/schedule/googleCalendarSync/container"));
const GoogleCalendarSyncCallback = lazy(() => import("~/app/modules/schedule/calendarSyncResult/container"));
const Documents = lazy(() => import("../modules/documents/container"));
const WaiverOfLiability = lazy(() => import("../modules/waiverOfLiabilityConfiguration/container"));
const ServiceDocuments = lazy(() => import("../modules/serviceDocuments/container"));
const PetBreeds = lazy(() => import("../modules/petType/petBreeds/container"));
const EditAppointment = lazy(() => import("../modules/appointment/bookAppointment/container/editAppointment"));
const Messages = lazy(() => import("../modules/messages/container"));

const LoadingIndicator = (
  <div className="sub-loader-container">
    <Spinner
      as="span"
      animation="border"
      size="m"
      role="status"
      aria-hidden="true"
    />
  </div>
);

const RouterContent = () => {
  const collapsed = useSelector((state) => state.sidebar.collapsed);
  const location = useLocation();

  const isLoggedIn = useMemo(() => {
    const refreshToken = getCookie("refresh_token");
    const schemaName = getCookie("schema_name");
    return !!(refreshToken && schemaName);
  }, [location.pathname]);

  const isGoogleCalendarSyncRoute = useMemo(() => {
    return location.pathname.includes("/google-calendar-sync/");
  }, [location.pathname]);

  const shouldShowSidebar = isLoggedIn && !isGoogleCalendarSyncRoute;
  const shouldShowHeader = !isGoogleCalendarSyncRoute;

  return (
    <>
      <ToastContainer />
      {shouldShowHeader && <Header />}
      <div className={`content-wrap ${collapsed ? `sidebar-collapsed` : ``}`}>
        <div>
          {shouldShowSidebar && <Sidebar />}
          <Suspense fallback={LoadingIndicator}>
            <Switch>
              <PrivateRoute
                exact
                path="/"
                component={() => (
                  <Redirect to="/retailer/booking/configuration" />
                )}
              />
              <PrivateRoute
                exact
                path="/retailer/booking/configuration"
                component={BookingConfiguration}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/service-types"
                component={ServiceTypes}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/:petId?/general-pet-sizes"
                component={SetGeneralPetSizes}
              />

              <PrivateRoute
                exact
                path="/retailer/booking/configuration/pet-types"
                component={PetType}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-weight/"
                component={PetWeight}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/breeds/"
                component={PetBreeds}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-hair-length"
                component={PetHairLength}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-hair-texture"
                component={PetHairTexture}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-desired-hair-length"
                component={PetDesiredHairLength}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-name"
                component={PetName}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-combs-blades"
                component={PetCombsBlades}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-temperament"
                component={PetTemperament}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-vaccination-records"
                component={PetVaccinationRecords}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-allergies"
                component={PetAllergies}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-vet"
                component={PetVet}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-docs"
                component={PetDocs}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-emergency-contact"
                component={PetEmergencyContact}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId?/pet-decease-date"
                component={PetDeceaseDate}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/pet-types/:petId/information"
                component={PetTypeInformation}
              />

              <PrivateRoute
                path="/retailer/booking/configuration/feeding-information"
                component={FeedingInformation}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/extended-behavior-information"
                component={ExtendedBehaviorInformation}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/shampoo-selections"
                component={ShampooSelections}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/cologne-selections"
                component={CologneSelections}
              />
              <PrivateRoute
                exact
                path="/retailer/booking/configuration/attendants"
                component={AttendantList}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/attendants/create"
                component={AddNewAttendant}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/attendants/:id/edit"
                component={EditAttendant}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/add-on-services"
                component={AddOnServices}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/documents"
                component={Documents}
              />
              <PrivateRoute
                exact
                path="/retailer/booking/configuration/waiver-of-liability"
                component={WaiverOfLiability}
              />
              <PrivateRoute path="/success" component={Success} />
              <PrivateRoute path="/error" component={ErrorPage} />
              <PrivateRoute
                path="/retailer/booking/schedule/create"
                component={BookAppointment}
              />
              <PrivateRoute
                path="/retailer/booking/schedule/edit/:id"
                component={EditAppointment}
                exact
              />
              <PrivateRoute
                path="/retailer/booking/schedule/rebook/:id"
                component={EditAppointment}
                exact
              />
              <PrivateRoute
                exact
                path="/retailer/booking/configuration/venues"
                component={VenueList}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/venues/create"
                component={AddNewVenue}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/venues/:id/edit"
                component={EditVenue}
              />
              <PrivateRoute
                exact
                path="/retailer/booking/appointments"
                component={Appointments}
              />
              <PrivateRoute
                exact
                path="/retailer/booking/schedule"
                component={Schedule}
              />
              <PrivateRoute
                exact
                path="/retailer/booking/availabilityPage"
                component={Availability}
              />
              <PrivateRoute
                path="/retailer/booking/schedule/:id/details"
                component={TodaysAppointmentDetails}
              />

              <PrivateRoute
                exact
                path="/retailer/booking/configuration/services"
                component={ServicesList}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/services/create"
                component={AddNewService}
              />
              <PrivateRoute
                path="/retailer/booking/configuration/services/:id/edit"
                component={EditService}
              />

              <PrivateRoute
                path="/retailer/customers/:customerId/pet-list"
                component={CustomerPetList}
              />
              <PrivateRoute
                path="/retailer/customers/:customerId/pet/create"
                component={CustomerAddNewPet}
              />
              <PrivateRoute
                path="/retailer/customers/:customerId/pet/:petId/edit"
                component={CustomerPetDetails}
              />
              <PrivateRoute
                path="/retailer/customers/:customerId/services-history-list"
                component={CustomerServicesHistoryList}
              />
              <PrivateRoute
                path="/retailer/customers/:customerId/services-history/:scheduleId/details/general"
                component={CustomerServicesHistoryDetailsGeneral}
              />
              <PrivateRoute
                path="/retailer/customers/:customerId/services-history/:scheduleId/details/notes"
                component={CustomerServicesHistoryDetailsNotes}
              />

              <PrivateRoute
                path="/retailer/booking/schedule/:date/list"
                component={TodaysAppointmentList}
              />
              <PrivateRoute
                path="/retailer/customers/:customerId/service-documents"
                component={ServiceDocuments}
              />
              <PrivateRoute path="/batch-actions" component={BatchActions} />
              <PrivateRoute path="/retailer/messages" component={Messages} />
              <Route
                exact
                path="/retailer/booking/google-calendar-sync/collab"
                component={GoogleCalendarSyncCollab}
              />
              <Route
                exact
                path="/retailer/booking/google-calendar-sync/callback"
                component={GoogleCalendarSyncCallback}
              />
            </Switch>
          </Suspense>
        </div>
      </div>
    </>
  );
};

const Router = () => {
  return (
    <BrowserRouter>
      <RouterContent />
    </BrowserRouter>
  );
};

Router.propTypes = {
  fetchEcommAuthToken: PropTypes.func,
  authentication_state: PropTypes.object,
};

export default Router;